<script setup lang="ts">
import ArrowRight from '@/assets/icon/arrow-right.svg'
import Star from '@/assets/icon/star.svg'
import { LocaleCode } from '@packages/i18n'
import { formatScoreValue } from '~/utils/formatters'
import type { WebHomeExchangeRankingCategory, WebHomeExchangeRankingLabel } from '~/server/home'

const DEFAULT_VISIBLE_COUNT = 5

type HighRiskItem = {
  alias: any
  id: string
  name: string
  logo: string
  score: string | number
  scoreIcon: string
  detailUrl?: string
  labels?: WebHomeExchangeRankingLabel[]
}

const props = defineProps<{
  data?: Record<string, unknown> | WebHomeExchangeRankingCategory
}>()

const alertPayload = computed(() => props.data ?? {})

const { localePath, t } = useAppI18n()
const matomo = useMatomo()
const alertTitle = computed(() => {
  const title = alertPayload.value.title
  const name = alertPayload.value.name
  if (typeof title === 'string' && title) return title
  if (typeof name === 'string' && name) return name
  return 'High Risk Exposure Alert'
})

const moreUrl = computed(() => {
  const url = alertPayload.value.moreUrl
  return typeof url === 'string' ? url : ''
})

const riskList = computed<HighRiskItem[]>(() => {
  const list = alertPayload.value.list

  if (!Array.isArray(list)) {
    return []
  }

  return list
    .slice(0, DEFAULT_VISIBLE_COUNT)
    .map((item, index) => {
      if (!item || typeof item !== 'object') {
        return null
      }

      const record = item as Record<string, unknown>
      const labels = Array.isArray(record.labels) ? (record.labels as WebHomeExchangeRankingLabel[]) : []
      // const riskLabel = labels.find((label) => label.labelType === 109)
      // const yearsLabel = labels.find((label) => label.labelType === 101)
      const exchange = (record.exchange as Record<string, unknown> | undefined) ?? {}
      const score = (record.score as Record<string, unknown> | undefined) ?? {}
      const name = record.showName

      return {
        id:
          typeof record.traderCode === 'string' && record.traderCode
            ? record.traderCode
            : typeof record.alertId === 'string' && record.alertId
              ? record.alertId
              : `risk-alert-${index + 1}`,
        name,
        alias: record.alias,
        logo: record.icoSquare,
        labels: record.labels,
        // tag: typeof record.riskLabel === 'string' ? record.riskLabel : riskLabel?.content || '',
        // years: typeof record.operatingTime === 'string' ? record.operatingTime : yearsLabel?.content || '',
        score:
          typeof record.score === 'string'
            ? record.score
            : typeof score.valueText === 'string' && score.valueText
              ? score.valueText
              : '--',
        scoreIcon: typeof record.scoreIcon === 'string' ? record.scoreIcon : '',
      } satisfies HighRiskItem
    })
    .filter((item): item is HighRiskItem => item !== null)
})

const isOverflow = ref(false)

const checkOverflow = (event: MouseEvent) => {
  const target = event.currentTarget as HTMLElement
  if (target) {
    isOverflow.value = target.scrollWidth > target.clientWidth
  }
}

const toRisk = (item) => {
  matomo.trackEvent('home_blacklist', {
    af_name: item.name,
    af_code: item.id,
  })
}

const toMore = () => {
  matomo.trackEvent('home_blacklist_more')
}
</script>

<template>
  <div class="high-risk-alert-card" v-if="riskList.length > 0">
    <div class="header">
      <div class="header-title">
        <span class="text-[#E2483D]">{{ t('83878') }}</span>
        {{ alertTitle }}
      </div>
      <NuxtLink
        class="arrow-icon mr-[6px]"
        aria-label="View more high-risk alerts"
        :to="localePath({ name: 'best-exchanges-slug', params: { slug: 'scambrokerranking' } })"
        target="_blank"
        @click="toMore"
      >
        <ArrowRight class="size-[24px] rtl:rotate-180" />
      </NuxtLink>
    </div>

    <div class="list-container">
      <NuxtLink
        v-for="item in riskList"
        :key="item.id"
        class="list-item"
        :to="localePath({ name: 'exchange-detail', params: { slug: item?.alias } })"
        @click="toRisk(item)"
        target="_blank"
      >
        <CommonImage :src="item.logo" :alt="`${item.name} logo`" class="item-logo" loading="lazy" object-fit="cover" />

        <div class="item-info">
          <el-tooltip
            :content="item.name"
            placement="top"
            popper-class="exchange-name-tooltip"
            :show-after="300"
            :show-arrow="false"
            :offset="4"
            :disabled="!isOverflow"
          >
            <div class="item-name" @mouseenter="checkOverflow">{{ item.name }}</div>
          </el-tooltip>
          <div class="item-meta h-[16px] flex-wrap content-start overflow-hidden">
            <span class="meta-tag max-w-full truncate" :style="{ color: formatColor(item?.labels?.[0]?.fontColor) }">{{
              item?.labels?.[0]?.content
            }}</span>
            <div class="flex shrink-0 items-center gap-[8px]" v-if="item?.labels?.length === 2">
              <span class="meta-divider shrink-0"></span>
              <span class="meta-years shrink-0" :style="{ color: formatColor(item?.labels?.[1]?.fontColor) }">{{
                item.labels?.[1]?.content
              }}</span>
            </div>
          </div>
        </div>

        <div class="score-badge">
          <CommonImage v-if="item.scoreIcon" :src="item.scoreIcon" alt="" class="star-icon" loading="lazy" />
          <Star v-else class="star-icon" />
          <span class="score-value">{{ formatScoreValue(item.score) }}</span>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>

<style scoped>
.high-risk-alert-card {
  width: 348px;
  padding: 8px 19px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 12px;
  border: 1px solid rgba(11, 18, 40, 0.14);
  background: linear-gradient(180deg, #fff5f6 0%, #fff 100%);
  box-sizing: border-box;
  overflow: hidden;
}

.header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 15px;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
}

.header-title {
  color: #292a2e;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.arrow-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}

.list-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.list-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 -19px;
  height: 66px;
  padding: 13px 19px;
  cursor: pointer;
}

.list-item::before {
  position: absolute;
  inset: 0 8px;
  z-index: 0;
  content: '';
  border-radius: 4px;
  background-color: transparent;
  pointer-events: none;
  transition: background-color 0.2s ease;
}

.list-item > * {
  position: relative;
  z-index: 1;
}

.list-item:hover::before {
  background-color: var(--color-brand-Foreground, #fff);
}

.item-logo {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  flex-shrink: 0;
  overflow: hidden;
}

.item-info {
  flex: 1;
  margin-inline-start: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.item-name {
  font-size: 16px;
  font-weight: 500;
  color: #292a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 22px;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  line-height: 16px;
}

.meta-tag {
  font-size: 11px;
  font-weight: 400;
}

.meta-divider {
  width: 0.5px;
  height: 8px;
  background-color: rgba(11, 18, 40, 0.14);
}

.meta-years {
  color: #505258;
  font-size: 11px;
  font-weight: 400;
}

.score-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 72px;
  gap: 4px;
  background-color: #f8f8f8;
  border-radius: 100px;
  flex-shrink: 0;
}

.star-icon {
  width: 14px;
  height: 14px;
}

.score-value {
  font-family: WikiFX-Medium;
  color: #292a2e;
  font-size: 16px;
  font-weight: 500;
}

@media screen and (min-width: 800px) and (max-width: 1200px) {
  .high-risk-alert-card {
    width: 100%;
  }
}

@media screen and (max-width: 799px) {
  .high-risk-alert-card {
    width: 800px;
  }
}
</style>
